import { PROJECTS_SAVE_RESULT } from "../constants/projects-save-results.ts"
import type {
  ArchiveQuestion,
  PendingCommitment,
  PendingSignature
} from "./services.types.ts"

interface CommitmentsRefusal {
  type?: string
  pending_commitments?: PendingCommitment[]
  pending_signatures?: PendingSignature[]
}

/** What the user agrees to give up by confirming: meetings and signature requests together. */
export const ARCHIVE_ACKNOWLEDGEMENT = {
  cancel_appointments: true,
  void_signature_requests: true
} as const

/** The meetings and signature requests a refused close would drop, or null for any other refusal. */
export const pendingCommitmentsFrom = (
  payload: unknown
): ArchiveQuestion | null => {
  const refusal = payload as CommitmentsRefusal | null | undefined
  if (refusal?.type !== PROJECTS_SAVE_RESULT.PENDING_COMMITMENTS) return null

  const pendingCommitments = refusal.pending_commitments ?? []
  const pendingSignatures = refusal.pending_signatures ?? []
  // Nothing listed leaves nothing to confirm, so it is an ordinary error.
  if (!pendingCommitments.length && !pendingSignatures.length) return null
  return { pendingCommitments, pendingSignatures }
}

/** Checked before the handled-error path, so its toast never buries the question. */
export const rejectPendingCommitments = <R>(
  payload: unknown,
  rejectWithValue: (value: ArchiveQuestion) => R
): R | null => {
  const question = pendingCommitmentsFrom(payload)
  return question ? rejectWithValue(question) : null
}

/** The same question, read off a rejected thunk. */
export const commitmentsFromRejection = (
  error: unknown
): ArchiveQuestion | undefined => {
  const rejection = error as Partial<ArchiveQuestion> | undefined
  const pendingCommitments = rejection?.pendingCommitments ?? []
  const pendingSignatures = rejection?.pendingSignatures ?? []
  return pendingCommitments.length || pendingSignatures.length
    ? { pendingCommitments, pendingSignatures }
    : undefined
}
